import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChefHat, Maximize, X, WifiOff, BellRing, Check, ChevronLeft, ChevronRight, CalendarDays, CalendarRange } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { buildDay, musicLineText, offLineText } from '../components/DailySheetV2';
import { collectServiceEntries } from '../components/FunctionSheetRenderer';
import { todayKey, toLocalDateKey, addDays, parseLocalDate } from '../lib/dateHelpers';
import { fetchEvents, fetchTableBookings } from '../lib/supabaseService';
import { useOccupancy, formatOcc } from '../lib/occupancy';
import { hasDietaryAnswer, effectiveFoodTime } from '../lib/eventCompleteness';

/**
 * Kitchen Live — always-on view for a tablet at the pass (Oct 2026, Phase 2).
 * Same data as the printed function sheet (buildDay), updated live through the
 * app's realtime subscriptions plus a 2-minute safety refetch.
 * View only. A change banner lists anything that changed since the kitchen last
 * tapped "Seen" (covers, times, dietary, menu, new or cancelled bookings).
 */

const hhmm = (t) => (typeof t === 'string' ? t.slice(0, 5) : '');
const mins = (t) => { const [h, m] = hhmm(t).split(':').map(Number); return Number.isFinite(h) ? h * 60 + (m || 0) : null; };
const lines = (txt) => String(txt || '').split(/\r?\n/).map((l) => l.replace(/^[\s•\-*•]+/, '').trim()).filter(Boolean);
const num = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : 0; };
const cleanName = (n) => String(n || '').replace(/\s*\(from table\)\s*$/i, '').trim();
const safeGet = (k) => { try { return JSON.parse(window.localStorage.getItem(k) || 'null'); } catch (_) { return null; } };
const safeSet = (k, v) => { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch (_) { /* private mode */ } };

function dietText(e) {
  return [
    num(e.dietaryVeg) && `${num(e.dietaryVeg)} vegetarian`,
    num(e.dietaryVegan) && `${num(e.dietaryVegan)} vegan`,
    num(e.dietaryCoeliac) && `${num(e.dietaryCoeliac)} coeliac/GF`,
    e.dietaryAllergens && `Allergens: ${e.dietaryAllergens}`,
    e.dietaryRequirements,
  ].filter(Boolean).join(' · ');
}

/** What the kitchen cares about for each item, so changes can be spotted. */
function snapshot(day) {
  const snap = {};
  day.dayEvents.forEach((e) => {
    snap[`e:${e.id}`] = {
      label: cleanName(e.name),
      covers: e.foodServiceTime === 'none' ? 'no food' : String(e.guests || 0),
      time: hhmm(effectiveFoodTime(e) && effectiveFoodTime(e) !== 'none' ? effectiveFoodTime(e) : e.startTime),
      dietary: dietText(e),
      menu: String(e.menuNotes || '').trim(),
    };
  });
  day.dayTables.filter((b) => !b.isEvent).forEach((b) => {
    snap[`t:${b.id}`] = {
      label: `${cleanName(b.guestName)} (${day.area(b.venue)})`,
      covers: String(b.covers || 0),
      time: hhmm(b.time),
      dietary: String(b.specialRequirements || '').trim(),
      menu: b.packageName || '',
    };
  });
  return snap;
}

function diffSnapshots(base, cur) {
  const out = [];
  const nice = { covers: 'covers', time: 'time', dietary: 'dietary / requirements', menu: 'menu / package' };
  Object.keys(cur).forEach((k) => {
    const c = cur[k], b = base[k];
    if (!b) { out.push({ kind: 'new', text: `New: ${c.time} · ${c.label} · ${c.covers} covers` }); return; }
    ['covers', 'time'].forEach((f) => { if (b[f] !== c[f]) out.push({ kind: 'change', text: `${c.label}: ${nice[f]} ${b[f] || '—'} → ${c[f] || '—'}` }); });
    ['dietary', 'menu'].forEach((f) => { if (b[f] !== c[f]) out.push({ kind: 'change', text: `${c.label}: ${nice[f]} updated${c[f] ? ` — ${c[f].slice(0, 80)}` : ' (now blank)'}` }); });
  });
  Object.keys(base).forEach((k) => { if (!cur[k]) out.push({ kind: 'gone', text: `Removed / cancelled: ${base[k].time} · ${base[k].label} · ${base[k].covers} covers` }); });
  return out;
}

export default function KitchenLive({ onNavigate }) {
  const { events = [], tableBookings = [], spaBookings = [], venues = [], restaurantPackages = [], setEvents, setTableBookings, dbLoading } = useAppContext();
  const [offset, setOffset] = useState(0);       // days from today (day view)
  const [mode, setMode] = useState('day');        // 'day' | 'week'
  const [now, setNow] = useState(new Date());
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [seenTick, setSeenTick] = useState(0);
  const wakeLock = useRef(null);

  const dateStr = toLocalDateKey(addDays(todayKey(), offset));
  const isToday = offset === 0;
  const dayLabel = (off) => (off === 0 ? 'Today' : off === 1 ? 'Tomorrow' : off === -1 ? 'Yesterday' : null);

  // Clock (drives "next up" and dimming past services)
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  // Online / offline
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Data changed (realtime or refetch) → note the time
  useEffect(() => { setLastUpdated(new Date()); }, [events, tableBookings, spaBookings]);

  // Safety net: refetch every 2 minutes in case a realtime message was missed
  useEffect(() => {
    const t = setInterval(async () => {
      if (!navigator.onLine) return;
      try {
        const [ev, tb] = await Promise.all([fetchEvents(), fetchTableBookings()]);
        if (Array.isArray(ev) && setEvents) setEvents(ev);
        if (Array.isArray(tb) && setTableBookings) setTableBookings(tb);
        setLastUpdated(new Date());
      } catch (_) { /* keep showing last data; offline banner covers it */ }
    }, 120000);
    return () => clearInterval(t);
  }, [setEvents, setTableBookings]);

  // Keep the tablet awake while this page is open (re-acquire after tab switch)
  useEffect(() => {
    const acquire = async () => {
      try { if ('wakeLock' in navigator && document.visibilityState === 'visible') wakeLock.current = await navigator.wakeLock.request('screen'); } catch (_) { /* not supported */ }
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => { document.removeEventListener('visibilitychange', acquire); try { wakeLock.current && wakeLock.current.release(); } catch (_) { /* ignore */ } };
  }, []);

  const day = useMemo(
    () => buildDay(dateStr, { events, tableBookings, spaBookings, packages: restaurantPackages, venues }),
    [dateStr, events, tableBookings, spaBookings, restaurantPackages, venues]
  );

  // ── Week view: Monday–Sunday of the week containing the viewed day ──
  const weekDays = useMemo(() => {
    if (mode !== 'week') return [];
    const d = parseLocalDate(dateStr);
    const monOff = offset - ((d.getDay() + 6) % 7);
    return Array.from({ length: 7 }, (_, i) => {
      const off = monOff + i;
      const ds = toLocalDateKey(addDays(todayKey(), off));
      return { off, ds, day: buildDay(ds, { events, tableBookings, spaBookings, packages: restaurantPackages, venues }) };
    });
  }, [mode, dateStr, offset, events, tableBookings, spaBookings, restaurantPackages, venues]);

  // ── In-house figures (entered by reception on Home / function sheets) ──
  const occDates = useMemo(() => (mode === 'week' ? weekDays.map((w) => w.ds) : [dateStr]), [mode, weekDays, dateStr]);
  const { occ, reload: reloadOcc } = useOccupancy(occDates);
  useEffect(() => { const t = setInterval(reloadOcc, 2 * 60 * 1000); return () => clearInterval(t); }, [reloadOcc]);
  const dayOcc = occ[dateStr];

  // ── Change alerts ──
  const snap = useMemo(() => snapshot(day), [day]);
  const baseKey = `kitchenLive.seen.${dateStr}`;
  const stored = useMemo(() => safeGet(baseKey), [baseKey, seenTick]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only take the first baseline once the app's data has loaded — otherwise an
  // empty snapshot is saved and everything shows as "New" when data arrives.
  useEffect(() => { if (!stored && !dbLoading) { safeSet(baseKey, { at: new Date().toISOString(), snap }); setSeenTick((x) => x + 1); } }, [stored, baseKey, snap, dbLoading]);
  // Tidy up baselines for dates more than a week old so browser storage doesn't fill up
  useEffect(() => {
    try {
      const cutoff = toLocalDateKey(addDays(todayKey(), -7));
      Object.keys(window.localStorage).filter((k) => k.startsWith('kitchenLive.seen.') && k.slice(17) < cutoff).forEach((k) => window.localStorage.removeItem(k));
    } catch (_) { /* storage unavailable */ }
  }, []);
  const changes = stored ? diffSnapshots(stored.snap || {}, snap) : [];
  const markSeen = () => { safeSet(baseKey, { at: new Date().toISOString(), snap }); setSeenTick((x) => x + 1); };

  // ── Timeline: table covers by time + each event's food time + event service times ──
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const timeline = useMemo(() => {
    const items = [];
    day.slots.forEach(([t, s]) => items.push({ key: `s${t}`, time: t, covers: s.covers, what: s.what.join(', '), kind: 'slot' }));
    day.dayEvents.forEach((e) => {
      collectServiceEntries(e).filter((x) => x.time).forEach((x, i) => items.push({
        key: `v${e.id}${i}`, time: hhmm(x.time), covers: null,
        what: `${x.description}${x.location ? ` (${x.location})` : ''} — ${cleanName(e.name)}`, kind: 'service',
      }));
    });
    return items.sort((a, b) => a.time.localeCompare(b.time) || (a.kind === 'slot' ? -1 : 1));
  }, [day]);
  const nextIdx = isToday ? timeline.findIndex((x) => (mins(x.time) ?? 0) >= nowMins - 15) : -1;
  const next = nextIdx >= 0 ? timeline[nextIdx] : null;
  const minsToNext = next ? (mins(next.time) - nowMins) : null;

  // Restaurant packages to prepare (excludes à la carte and event rows)
  const pkgCounts = useMemo(() => {
    const m = {};
    day.dayTables.filter((b) => !b.isEvent && b.packageName).forEach((b) => { m[b.packageName] = (m[b.packageName] || 0) + (b.covers || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [day]);
  const bigTables = day.dayTables.filter((b) => !b.isEvent && (b.covers || 0) >= 8);
  const foodEvents = day.dayEvents.filter((e) => e.foodServiceTime !== 'none');
  const drinksOnly = day.dayEvents.filter((e) => e.foodServiceTime === 'none');

  const goFull = () => { try { document.documentElement.requestFullscreen && document.documentElement.requestFullscreen(); } catch (_) { /* ignore */ } };
  const exit = () => { try { document.fullscreenElement && document.exitFullscreen(); } catch (_) { /* ignore */ } onNavigate && onNavigate('dashboard'); };
  const longDate = parseLocalDate(dateStr)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });
  const clock = now.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' });
  const upd = lastUpdated.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950 text-slate-100 overflow-y-auto no-print">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-700 px-4 py-2 flex flex-wrap items-center gap-3">
        <ChefHat size={26} className="text-emerald-400" />
        <div className="text-xl font-bold tracking-wide">
          KITCHEN · {mode === 'week' && weekDays.length
            ? `Week of ${parseLocalDate(weekDays[0].ds).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })} – ${parseLocalDate(weekDays[6].ds).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}`
            : `${dayLabel(offset) ? `${dayLabel(offset)} · ` : ''}${longDate}`}
        </div>
        <div className="flex items-center gap-1 ml-2">
          <button onClick={() => setOffset((o) => o - (mode === 'week' ? 7 : 1))} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title={mode === 'week' ? 'Previous week' : 'Previous day'}><ChevronLeft size={22} /></button>
          <button onClick={() => setOffset(0)} className={`px-4 py-1.5 rounded-lg text-base font-semibold ${isToday ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'}`}>Today</button>
          <button onClick={() => setOffset((o) => o + (mode === 'week' ? 7 : 1))} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title={mode === 'week' ? 'Next week' : 'Next day'}><ChevronRight size={22} /></button>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-slate-600">
          <button onClick={() => setMode('day')} className={`flex items-center gap-1.5 px-3 py-1.5 text-base font-semibold ${mode === 'day' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300'}`}><CalendarDays size={18} />Day</button>
          <button onClick={() => setMode('week')} className={`flex items-center gap-1.5 px-3 py-1.5 text-base font-semibold ${mode === 'week' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300'}`}><CalendarRange size={18} />Week</button>
        </div>
        <div className="ml-auto flex items-center gap-4">
          {online
            ? <span className="flex items-center gap-2 text-sm text-emerald-400"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />Live · updated {upd}</span>
            : <span className="flex items-center gap-2 text-sm font-bold text-red-400"><WifiOff size={16} />OFFLINE · data as of {upd}</span>}
          <span className="text-3xl font-bold tabular-nums">{clock}</span>
          <button onClick={goFull} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title="Full screen"><Maximize size={20} /></button>
          <button onClick={exit} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700" title="Exit"><X size={20} /></button>
        </div>
      </div>

      {!online && (
        <div className="bg-red-700 text-white text-center font-bold py-2">Connection lost — showing the last data received at {upd}. Check with reception before prepping changes.</div>
      )}

      {mode === 'week' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 p-4">
          {weekDays.map(({ off, ds, day: d }) => {
            const fe = d.dayEvents.filter((e) => e.foodServiceTime !== 'none');
            const teaCovers = d.teaToday.reduce((s, b) => s + (b.covers || 0), 0);
            const pk = {};
            d.dayTables.filter((b) => !b.isEvent && b.packageName).forEach((b) => { pk[b.packageName] = (pk[b.packageName] || 0) + (b.covers || 0); });
            const past = off < 0;
            return (
              <button key={ds} onClick={() => { setOffset(off); setMode('day'); }}
                className={`flex flex-col justify-start text-left rounded-xl border p-3 transition hover:border-emerald-400 ${off === 0 ? 'border-emerald-400 bg-emerald-900/30' : 'border-slate-700 bg-slate-900'} ${past ? 'opacity-50' : ''}`}>
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-400 h-4">{dayLabel(off) || ''}</div>
                <div className="text-lg font-bold whitespace-nowrap">{parseLocalDate(ds).toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                <div className="text-4xl font-bold tabular-nums leading-tight">{d.eventCovers + d.tableCovers}</div>
                <div className="text-xs uppercase tracking-wider text-slate-400">food covers · {d.tableCount} tables ({d.tableCovers})</div>
                <div className={`text-sm font-semibold mb-2 ${occ[ds] ? 'text-sky-300' : 'text-amber-400'}`}>🛏 {formatOcc(occ[ds])}</div>
                {fe.length === 0 && <div className="text-sm text-slate-500">No events</div>}
                {fe.map((e) => (
                  <div key={e.id} className="text-sm leading-snug mb-1">
                    <b className="tabular-nums">{hhmm(effectiveFoodTime(e) || e.startTime)}</b> {cleanName(e.name)} <b className="text-amber-300">{e.guests || 0}</b>
                    {dietText(e) && <span className="ml-1 text-red-300" title={dietText(e)}>⚠ diet</span>}
                    {e.tickConflicts && <span className="ml-1 text-amber-300" title={e.tickConflicts.join('; ')}>⚠ check</span>}
                  </div>
                ))}
                {d.musicEvents.length > 0 && <div className="text-xs text-slate-500">♪ {d.musicEvents.length} music only (no food)</div>}
                {d.offEvents.length > 0 && <div className="text-xs text-slate-500">{d.offEvents.length} not running</div>}
                {teaCovers > 0 && <div className="mt-1 text-sm text-pink-300 font-semibold">Afternoon tea: {teaCovers}</div>}
                {Object.keys(pk).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">{Object.entries(pk).map(([n, c]) => <span key={n} className="text-xs bg-slate-800 rounded px-1.5 py-0.5">{c}× {n}</span>)}</div>
                )}
              </button>
            );
          })}
          <div className="col-span-full text-sm text-slate-500">Tap a day to open it.</div>
        </div>
      )}

      {mode === 'day' && (<>
      {/* Change alerts */}
      {changes.length > 0 && (
        <div className="mx-4 mt-3 rounded-xl border-4 border-amber-400 bg-amber-950/60 p-3">
          <div className="flex items-start gap-3">
            <BellRing size={28} className="text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-lg font-bold text-amber-200">{changes.length} change{changes.length > 1 ? 's' : ''} since last seen{stored?.at ? ` (${new Date(stored.at).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })})` : ''}</div>
              <ul className="mt-1 space-y-0.5 text-base">
                {changes.slice(0, 8).map((c, i) => (
                  <li key={i} className={c.kind === 'gone' ? 'text-red-300 line-through decoration-2' : c.kind === 'new' ? 'text-emerald-300' : 'text-amber-100'}>{c.text}</li>
                ))}
                {changes.length > 8 && <li className="text-amber-300">+ {changes.length - 8} more</li>}
              </ul>
            </div>
            <button onClick={markSeen} className="shrink-0 flex items-center gap-2 bg-amber-400 text-slate-900 font-bold text-lg px-5 py-3 rounded-xl active:scale-95"><Check size={22} />Seen</button>
          </div>
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-4 mt-3">
        <Tile label="Food covers" value={day.eventCovers + day.tableCovers} sub={`${foodEvents.length} events · ${day.tableCount} tables`} />
        <Tile label="Events" value={day.eventCovers} sub={`${foodEvents.length} with food${drinksOnly.length ? ` · ${drinksOnly.length} drinks only` : ''}`} />
        <Tile label="Restaurant" value={day.tableCovers} sub={`${day.tableCount} bookings`} />
        {isToday
          ? <Tile label="Next up" value={next ? next.time : '—'} sub={next ? `${next.covers != null ? `${next.covers} covers · ` : ''}${minsToNext <= 0 ? 'now' : `in ${minsToNext >= 60 ? `${Math.floor(minsToNext / 60)}h ` : ''}${minsToNext % 60}m`}` : 'Nothing else today'} hot={next && minsToNext != null && minsToNext <= 30} />
          : <Tile label="Afternoon tea" value={day.teaToday.reduce((s, b) => s + (b.covers || 0), 0)} sub={`${day.teaToday.length} bookings`} />}
        {dayOcc
          ? <Tile label="In-house tonight" value={dayOcc.guests} sub={`guests · ${dayOcc.rooms} room${dayOcc.rooms === 1 ? '' : 's'}`} />
          : <div className="rounded-xl px-4 py-3 border border-amber-500 bg-amber-950/40">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-300">In-house tonight</div>
              <div className="text-2xl font-bold leading-tight text-amber-200 mt-1">{dayOcc === null ? 'Not entered' : '…'}</div>
              <div className="text-sm text-amber-300/80">{dayOcc === null ? 'ask reception' : ''}</div>
            </div>}
      </div>

      {/* Afternoon tea */}
      {(day.teaToday.length > 0 || (offset >= 0 && day.teaTomorrow.length > 0)) && (
        <div className="grid md:grid-cols-2 gap-3 px-4 mt-3">
          {day.teaToday.length > 0 && (
            <div className="rounded-xl border-2 border-pink-500 bg-pink-950/40 p-3">
              <div className="text-sm font-bold uppercase tracking-wider text-pink-300">Afternoon tea {isToday ? 'today' : `on ${parseLocalDate(dateStr).toLocaleDateString('en-IE', { weekday: 'long' })}`}</div>
              {day.teaToday.map((b) => <div key={b.id} className="text-lg"><b>{hhmm(b.time)} · {b.covers}</b> · {cleanName(b.guestName)} · {day.area(b.venue)}</div>)}
            </div>
          )}
          {offset >= 0 && day.teaTomorrow.length > 0 && (
            <div className="rounded-xl border-2 border-dashed border-red-500 bg-red-950/40 p-3">
              <div className="text-sm font-bold uppercase tracking-wider text-red-300">⚠ {isToday ? 'Order today for tomorrow' : `Order this day for ${parseLocalDate(day.tomorrowStr).toLocaleDateString('en-IE', { weekday: 'long' })}`}</div>
              {day.teaTomorrow.map((b) => <div key={b.id} className="text-lg"><b>{hhmm(b.time)} · {b.covers} × {b.packageName || 'Afternoon Tea'}</b> · {cleanName(b.guestName)}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-4 px-4 py-4">
        {/* Timeline */}
        <div className="lg:col-span-2">
          <div className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-2">Service timeline</div>
          {timeline.length === 0 && <div className="text-slate-500 text-lg">Nothing booked.</div>}
          <div className="space-y-1.5">
            {timeline.map((x, i) => {
              const past = isToday && (mins(x.time) ?? 0) < nowMins - 15;
              const isNext = i === nextIdx;
              return (
                <div key={x.key} className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${isNext ? 'border-emerald-400 bg-emerald-900/40' : 'border-slate-800 bg-slate-900'} ${past ? 'opacity-40' : ''}`}>
                  <div className="text-2xl font-bold tabular-nums w-20 shrink-0">{x.time}</div>
                  {x.covers != null
                    ? <div className={`text-2xl font-bold w-16 text-right shrink-0 ${x.covers >= 20 ? 'text-amber-300' : ''}`}>{x.covers}</div>
                    : <div className="w-16 shrink-0 text-right text-xs uppercase text-sky-300 font-semibold">service</div>}
                  <div className={`text-base leading-tight ${x.kind === 'service' ? 'text-sky-200' : 'text-slate-300'}`}>{x.what}</div>
                </div>
              );
            })}
          </div>
          {(pkgCounts.length > 0 || bigTables.length > 0) && (
            <div className="mt-4 rounded-xl bg-slate-900 border border-slate-700 p-3">
              {pkgCounts.length > 0 && (<>
                <div className="text-sm font-bold uppercase tracking-wider text-slate-400">Restaurant packages</div>
                <div className="flex flex-wrap gap-2 mt-1">{pkgCounts.map(([n, c]) => <span key={n} className="bg-slate-800 rounded px-2 py-1 text-base"><b>{c}</b> × {n}</span>)}</div>
              </>)}
              {bigTables.length > 0 && (<>
                <div className="text-sm font-bold uppercase tracking-wider text-slate-400 mt-3">Large tables (8+)</div>
                {bigTables.map((b) => (
                  <div key={b.id} className="text-base mt-0.5"><b>{hhmm(b.time)} · {b.covers}</b> · {cleanName(b.guestName)} · {day.area(b.venue)}{b.packageName ? ` · ${b.packageName}` : ''}{b.specialRequirements || b.notes ? <span className="text-slate-400"> — {[b.specialRequirements, b.notes].filter(Boolean).join(' · ')}</span> : null}</div>
                ))}
              </>)}
            </div>
          )}
        </div>

        {/* Events */}
        <div className="lg:col-span-3 space-y-3">
          <div className="text-sm font-bold uppercase tracking-wider text-slate-400">Events</div>
          {foodEvents.length === 0 && <div className="text-slate-500 text-lg">No events with food.</div>}
          {foodEvents
            .slice()
            .sort((a, b) => hhmm(effectiveFoodTime(a) || a.startTime).localeCompare(hhmm(effectiveFoodTime(b) || b.startTime)))
            .map((e) => {
              const menu = lines(e.menuNotes);
              const notes = lines(e.notes).filter((l) => !/^upgraded from table booking\.?$/i.test(l));
              const diet = dietText(e);
              const services = collectServiceEntries(e).filter((x) => x.time || x.description);
              return (
                <div key={e.id} className="rounded-xl bg-slate-900 border border-slate-700 overflow-hidden">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 bg-slate-800 px-3 py-2">
                    <span className="text-2xl font-bold tabular-nums">{hhmm(effectiveFoodTime(e) || e.startTime)}</span>
                    <span className="text-xl font-bold">{e.type === 'wedding' ? '💒 ' : ''}{cleanName(e.name)}</span>
                    <span className="text-slate-400">{day.venueName(e.venue_id)}</span>
                    <span className="ml-auto text-2xl font-bold text-amber-300">{e.guests || 0} covers</span>
                  </div>
                  {e.tickConflicts && e.tickConflicts.length > 0 && (
                    <div className="bg-amber-500 text-amber-950 font-bold px-3 py-1.5 text-base">⚠ Check with reception: {e.tickConflicts.join('; ')}</div>
                  )}
                  <div className="p-3 grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Menu</div>
                      {menu.length
                        ? <ul className="text-lg leading-snug mt-0.5">{menu.map((l, i) => <li key={i}>• {l}</li>)}</ul>
                        : <div className="text-slate-500 text-lg">No menu entered</div>}
                      {notes.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Notes</div>
                          <div className="text-base text-slate-300 leading-snug">{notes.slice(0, 6).join(' · ')}{notes.length > 6 ? ' …' : ''}</div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {diet
                        ? <div className="rounded-lg border-2 border-red-500 bg-red-950/50 px-3 py-2 text-lg font-bold text-red-200">⚠ {diet}</div>
                        : e.dietaryNA ? <div className="rounded-lg border-2 border-slate-600 px-3 py-2 text-base text-slate-300">Dietary: N/A – no meal chosen</div>
                        : hasDietaryAnswer(e) ? <div className="rounded-lg border-2 border-emerald-700 px-3 py-2 text-base text-emerald-300">No dietary requirements</div>
                        : <div className="rounded-lg border-2 border-amber-600 px-3 py-2 text-base text-amber-300">No dietary info recorded</div>}
                      {services.length > 0 && (
                        <div>
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Service times</div>
                          {services.map((s, i) => <div key={i} className="text-base"><b className="tabular-nums">{s.time}</b> {s.description}{s.location ? ` (${s.location})` : ''}</div>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          {drinksOnly.length > 0 && (
            <div className="text-slate-400 text-base">Drinks / room only (no kitchen): {drinksOnly.map((e) => `${hhmm(e.startTime)} ${cleanName(e.name)} (${e.guests})`).join(' · ')}</div>
          )}
          {day.musicEvents.length > 0 && (
            <div className="text-slate-500 text-sm">♪ Music only (no food): {musicLineText(day.musicEvents, day.venueName)}</div>
          )}
          {day.offEvents.length > 0 && (
            <div className="text-slate-500 text-sm">Not running: {offLineText(day.offEvents)}</div>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}

function Tile({ label, value, sub, hot }) {
  return (
    <div className={`rounded-xl px-4 py-3 border ${hot ? 'border-emerald-400 bg-emerald-900/40' : 'border-slate-700 bg-slate-900'}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-4xl font-bold tabular-nums leading-tight">{value}</div>
      <div className="text-sm text-slate-400">{sub}</div>
    </div>
  );
}
